// Frente F3 (fidelidade-3, 2026-09-23) — resolve o Swatch da ficha de
// produto pra cor REAL (hex ou gradiente) em vez do circulo neutro com a
// inicial (achado #8 da auditoria fidelidade-3). O mapa vem de
// GET /api/settings/ui (catalog.settings.color_hex_map, migration
// 0025-color-hex-map.sql), semeado a partir dos nomes medidos ao vivo em
// sisplan.product_variations — nao e dado inventado.
//
// Cor sem entrada no mapa (estampa: PANTERA, MESCLA, TIE DYE, ESTAMPADO,
// UNICA, COLORS) continua caindo no circulo neutro com a inicial — decisao
// que a auditoria ja validou como certa pra esses casos.
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export interface UiSettings {
  color_hex_map: Record<string, string>;
}

/** Normaliza o nome da cor do jeito que a migration gravou as keys
 * (upper-case, trim) pra bater com o que vem do Sisplan em qualquer caixa. */
function normalizeColorName(name: string): string {
  return name.trim().toUpperCase();
}

/** Devolve o valor CSS de `background` (hex sólido ou linear-gradient) pra
 * uma cor, ou `null` se não houver mapeamento — nesse caso o chamador cai
 * no círculo neutro com inicial (mesmo comportamento de `ui/Swatch`). */
export function resolveColorBackground(
  name: string,
  map: Record<string, string> | undefined,
): string | null {
  if (!map) return null;
  const key = normalizeColorName(name);
  return map[key] ?? null;
}

/** true quando o valor resolvido é claro o bastante para precisar de borda
 * visível (ex: BRANCO), pra não sumir contra o fundo do card. Heurística
 * simples sobre hex sólido; gradiente e cores compostas sempre levam borda
 * neutra padrão (ja aplicada pelo componente chamador). */
export function isLightHex(hex: string): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  // Luminância relativa aproximada (sRGB, sem gamma correction — suficiente
  // pra decidir "clara o bastante pra precisar de borda mais escura").
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.85;
}

/** Hook: busca o mapa nome->hex uma vez por sessão de navegação (staleTime
 * longo — a paleta muda raramente e é editável via Configurações, que já
 * invalida a query certa ao salvar se algum dia precisar). Qualquer papel
 * autenticado pode ler (GET /api/settings/ui é AUTH_ONLY). */
export function useColorHexMap(): Record<string, string> {
  const { data } = useQuery<UiSettings>({
    queryKey: ["settings-ui"],
    queryFn: async () => {
      const r = await api.get<UiSettings>("/api/settings/ui");
      return r.data;
    },
    retry: false,
    staleTime: 10 * 60_000,
  });
  return data?.color_hex_map ?? {};
}
